import React, { useState, useEffect, useRef } from "react";
import "./Faq.css";

const CHEVRON = (
  <svg viewBox="0 0 24 24" aria-hidden="true" className="ico">
    <path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

export default function Faq() {
  const [open, setOpen] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const items = [
    {
      q: "Payment Methods Accepted",
      a: (
        <>
          Cash, Debit card, Discover, Visa and MasterCard.
        </>
      ),
    },
    {
      q: "Parking",
      a: <>Free parking.</>,
    },
    {
      q: "Amenities",
      a: <>Disabled Access.</>,
    },
    {
      q: "Spoken Languages",
      a: <>English.</>,
    },
    {
      q: "Kid Friendly",
      a: <>Yes.</>,
    },
    {
      q: "Special Instructions",
      a: (
        <>
          Please arrive <strong>15 minutes</strong> before your appointment to complete paperwork and to
          receive the full time of service. If you arrive late, time will be deducted as a courtesy to
          the next client.
        </>
      ),
    },
    {
      q: "Appointment Cancellation Policy",
      a: (
        <>
          <strong>Cancellation Policy:</strong> A minimum of <strong>24 hours' notice</strong> is required
          for all service cancellations. Failure to provide the necessary notice will result in a
          cancellation fee equivalent to <strong>50% of the service cost</strong>.
        </>
      ),
    },
  ];

  return (
    <section className="faq" aria-labelledby="faq-title" ref={sectionRef}>
      {/* Background Elements */}
      <div className="faq__bg-elements" aria-hidden="true">
        <div className="faq__bg-shape faq__bg-shape--1"></div>
        <div className="faq__bg-shape faq__bg-shape--2"></div>
        <div className="faq__bg-shape faq__bg-shape--3"></div>
      </div>

      <div className="faq__container">
        {/* Left column */}
        <header className={`faq__left ${isVisible ? 'is-visible' : ''}`}>
          <div className="faq__ornament" aria-hidden="true">
            <div className="faq__ornament-line"></div>
            <div className="faq__ornament-dot"></div>
            <div className="faq__ornament-line"></div>
          </div>
    
          <h1 id="faq-title" className="faq__title">
            <span className="faq__title-line">Frequently Asked</span>
            <span className="faq__title-line faq__title-line--accent">Questions</span>
          </h1>
          <p className="faq__description">
            Find quick answers to common questions about our services, policies, and what to expect during your visit.
          </p>
        </header>

        {/* Right column / Accordion */}
        <div className={`faq__right ${isVisible ? 'is-visible' : ''}`}>
          {/* FAQ items */}
          <div className="faq__list" role="list">
            {items.map((it, i) => {
              const id = `faq-item-${i}`;
              const expanded = open === i;
              return (
                <div 
                  className={`faq__item ${expanded ? "is-open" : ""} ${isVisible ? 'is-visible' : ''}`} 
                  role="listitem" 
                  key={i}
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  {/* Item Glow Effect */}
                  <div className="faq__item-glow" aria-hidden="true"></div>
                  
                  <button
                    className={`faq__summary ${expanded ? "is-open" : ""}`}
                    aria-expanded={expanded}
                    aria-controls={`${id}-panel`}
                    id={`${id}-button`}
                    onClick={() => setOpen(expanded ? -1 : i)}
                  >
                    <div className="faq__summary-content">
                      <div className="faq__question-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="20" height="20">
                          <circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="1.5"/>
                          <path d="M12 16v-4M12 8h.01" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <span className="faq__question-text">{it.q}</span>
                    </div>
                    <span className={`faq__chevron ${expanded ? "is-open" : ""}`}>
                      {CHEVRON}
                    </span>
                  </button>
                  <div
                    id={`${id}-panel`}
                    role="region"
                    aria-labelledby={`${id}-button`}
                    className={`faq__panel ${expanded ? "is-open" : ""}`}
                  >
                    <div className="faq__answer-content">
                      <div className="faq__answer-icon" aria-hidden="true">
                        <svg viewBox="0 0 24 24" width="16" height="16">
                          <path d="M9 12l2 2 4-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                      </div>
                      <div className="faq__answer-text">{it.a}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}